'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';

interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  related_id: string | null;
  related_table: string | null;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  loadNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  loadNotifications: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [communityInAppPrefs, setCommunityInAppPrefs] = useState<{
    notify_inapp_replies: boolean;
    notify_inapp_mentions: boolean;
    notify_inapp_badges: boolean;
    notify_inapp_reputation: boolean;
  } | null>(null);

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }
    try {
      setLoading(true);
      const [notifRes, prefsRes] = await Promise.all([
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_archived', false)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('community_email_preferences')
          .select('notify_inapp_replies, notify_inapp_mentions, notify_inapp_badges, notify_inapp_reputation')
          .eq('user_id', user.id)
          .maybeSingle(),
      ]);

      if (notifRes.error) throw notifRes.error;
      setNotifications(notifRes.data || []);
      // Use maybeSingle() so it returns null instead of error when no row exists
      if (prefsRes.data) {
        setCommunityInAppPrefs(prefsRes.data);
      } else {
        // Set default preferences if none exist
        setCommunityInAppPrefs({
          notify_inapp_replies: true,
          notify_inapp_mentions: true,
          notify_inapp_badges: true,
          notify_inapp_reputation: true,
        });
      }
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === payload.new.id ? { ...n, ...payload.new } : n))
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'community_notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const cn = payload.new as { id: string; user_id: string; type: string; metadata: Record<string, string>; is_read: boolean; created_at: string };
          const meta = cn.metadata || {};

          // Respect in-app notification preferences
          const prefs = communityInAppPrefs;
          const isReplyOrMention = ['thread_reply', 'post_reply', 'mention', 'accepted_answer', 'upvote_received', 'thread_subscription', 'category_subscription'].includes(cn.type);
          const isBadge = cn.type === 'badge_earned';
          const isReputation = cn.type === 'reputation_milestone';

          if (prefs) {
            if (isReplyOrMention && !prefs.notify_inapp_replies) return;
            if (isBadge && !prefs.notify_inapp_badges) return;
            if (isReputation && !prefs.notify_inapp_reputation) return;
          }

          let title = 'Community Update';
          let message = '';
          if (cn.type === 'thread_reply') {
            title = 'New reply to your thread';
            message = meta.reply_preview ? `"${meta.reply_preview}"` : `Someone replied to "${meta.thread_title || 'your thread'}"`;
          } else if (cn.type === 'post_reply') {
            title = 'New reply to your post';
            message = `Someone replied to your post in "${meta.thread_title || 'a thread'}"`;
          } else if (cn.type === 'mention') {
            title = 'You were mentioned';
            message = `${meta.mentioner_name || 'Someone'} mentioned you in "${meta.thread_title || 'a thread'}"`;
          } else if (cn.type === 'accepted_answer') {
            title = 'Your reply was accepted!';
            message = 'Your answer was marked as the accepted answer. +25 reputation!';
          } else if (cn.type === 'upvote_received') {
            title = 'Your post was upvoted';
            message = 'Someone upvoted your community post. +10 reputation!';
          } else if (cn.type === 'badge_earned') {
            title = `Badge Earned: ${meta.badge_name || 'New Badge'}`;
            message = meta.badge_description || 'You earned a new community badge!';
          } else if (cn.type === 'reputation_milestone') {
            title = 'Reputation Milestone!';
            message = `You reached ${meta.milestone} reputation points!`;
          } else if (cn.type === 'thread_subscription') {
            title = 'New activity in subscribed thread';
            message = `New activity in "${meta.thread_title || 'a thread'}" you follow`;
          } else if (cn.type === 'category_subscription') {
            title = 'New thread in subscribed category';
            message = `New thread "${meta.thread_title || 'Untitled'}" in ${meta.category_name || 'a category'} you follow`;
          }

          const mapped: Notification = {
            id: `community_${cn.id}`,
            user_id: cn.user_id,
            type: cn.type,
            title,
            message,
            related_id: meta.thread_id || null,
            related_table: 'community_notifications',
            is_read: cn.is_read,
            is_archived: false,
            created_at: cn.created_at,
          };
          setNotifications((prev) => [mapped, ...prev]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, communityInAppPrefs]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, loadNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
}
